import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Override the wrapper's display (defaults to `inline-block`). Useful for grid/flex children. */
  wrapperClassName?: string;
  delayMs?: number;
  /** If true, the tooltip content accepts pointer events (e.g. for buttons inside) and
   *  stays open while the cursor is hovered over it. */
  interactive?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  side = 'bottom',
  className,
  wrapperClassName,
  delayMs = 500,
  interactive = false,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
  const [offset, setOffset] = React.useState({ dx: 0, dy: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const tipRef = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useLayoutEffect(() => {
    if (!isVisible || !coords || !tipRef.current) {
      setOffset(prev => (prev.dx === 0 && prev.dy === 0) ? prev : { dx: 0, dy: 0 });
      return;
    }
    const rect = tipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 8;
    // Replicate Floating UI's shift(): slide the element along each axis and
    // clamp its position into the in-view range [pad, viewport - size - pad].
    // When the element is larger than the viewport, max collapses to pad so the
    // start/top edge stays visible (we never push the leading edge off-screen —
    // the single-pass if/else used to do exactly that).
    const shift = (start: number, size: number, viewport: number) => {
      const max = Math.max(pad, viewport - pad - size);
      return Math.min(Math.max(start, pad), max) - start;
    };
    const dx = shift(rect.left, rect.width, vw);
    const dy = shift(rect.top, rect.height, vh);
    setOffset(prev => (prev.dx === dx && prev.dy === dy) ? prev : { dx, dy });
  }, [coords, isVisible]);

  const positionFor = React.useCallback((rect: DOMRect) => {
    const centerX = rect.left + rect.width / 2;
    switch (side) {
      case 'top': return { top: rect.top - 8, left: centerX };
      case 'left': return { top: rect.top + rect.height / 2, left: rect.left - 8 };
      case 'right': return { top: rect.top + rect.height / 2, left: rect.right + 8 };
      case 'bottom':
      default: return { top: rect.bottom + 8, left: centerX };
    }
  }, [side]);

  const showTooltip = React.useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setCoords(positionFor(triggerRef.current.getBoundingClientRect()));
      }
      setIsVisible(true);
    }, delayMs);
  }, [delayMs, positionFor]);

  const hideTooltip = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
    setCoords(null);
  }, []);

  // Interactive mode: defer hide briefly so the cursor can cross the gap
  // between trigger and content without the tooltip disappearing.
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const scheduleHide = React.useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => hideTooltip(), 120);
  }, [hideTooltip]);
  const cancelHide = React.useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const showImmediate = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (triggerRef.current) {
      setCoords(positionFor(triggerRef.current.getBoundingClientRect()));
    }
    setIsVisible(true);
  }, [positionFor]);

  const toggleOnTap = React.useCallback(() => {
    if (isVisible) hideTooltip();
    else showImmediate();
  }, [isVisible, hideTooltip, showImmediate]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (tipRef.current?.contains(t)) return;
      hideTooltip();
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [isVisible, hideTooltip]);

  const transformClasses = {
    top: '-translate-x-1/2 -translate-y-full',
    bottom: '-translate-x-1/2',
    left: '-translate-x-full -translate-y-1/2',
    right: '-translate-y-1/2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-bg-tertiary dark:border-t-bg-quaternary',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-bg-tertiary dark:border-b-bg-quaternary',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-bg-tertiary dark:border-l-bg-quaternary',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-bg-tertiary dark:border-r-bg-quaternary',
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={wrapperClassName ?? 'inline-block'}
        onMouseEnter={() => { cancelHide(); showTooltip(); }}
        onMouseLeave={interactive ? scheduleHide : hideTooltip}
        onFocus={showTooltip}
        onBlur={interactive ? scheduleHide : hideTooltip}
        onClick={toggleOnTap}
      >
        {children}
      </div>

      {isVisible && coords && typeof document !== 'undefined' && createPortal(
        <div
          ref={tipRef}
          className={cn(
            'fixed z-[1070] px-3 py-2 text-sm font-medium text-text-primary dark:text-text-primary',
            'bg-bg-tertiary dark:bg-bg-quaternary border border-border-primary dark:border-border-primary',
            'rounded-lg shadow-lg max-w-[min(20rem,calc(100vw-1rem))]',
            interactive ? 'pointer-events-auto' : 'pointer-events-none',
            'animate-in fade-in-0 zoom-in-95 duration-200',
            transformClasses[side],
            className
          )}
          style={{ top: coords.top + offset.dy, left: coords.left + offset.dx }}
          role="tooltip"
          onMouseEnter={interactive ? cancelHide : undefined}
          onMouseLeave={interactive ? scheduleHide : undefined}
        >
          {content}
          <div className={cn('absolute w-0 h-0 border-4', arrowClasses[side])} />
        </div>,
        document.body
      )}
    </>
  );
};
